import { createContext, type ReactNode, useContext, useState } from "react";

export type ActionType = "DELETE" | "DISABLEACCOUNT";
export type AlertType = "CREATE" | ActionType;
type AlertKind = "Critical" | "Success";
interface AlertState {
  isVisible: boolean;
  type: AlertType | null;
  alertKind: AlertKind;
  message: string;
}

interface AlertContextType {
  isAlertVisible: boolean;
  alertType: AlertType | null;
  alertKind: AlertKind;
  message: string;
  showAlert: (params: {
    type: AlertType;
    alertKind?: AlertKind;
    message?: string;
  }) => void;
  hideAlert: () => void;
}

const AlertContext = createContext<AlertContextType | undefined>(undefined);

const defaultValue = {
  isVisible: false,
  type: null,
  alertKind: "Success" as AlertKind,
  message: "",
};

export function AlertProvider({ children }: { children: ReactNode }) {
  const [alertState, setAlertState] = useState<AlertState>(defaultValue);

  const showAlert = ({
    type,
    alertKind = "Success",
    message = "",
  }: {
    type: AlertType;
    alertKind?: AlertKind;
    message?: string;
  }) => {
    setAlertState({ isVisible: true, type, alertKind, message });
  };

  const hideAlert = () => {
    setAlertState(defaultValue);
  };

  return (
    <AlertContext.Provider
      value={{
        isAlertVisible: alertState.isVisible,
        alertType: alertState.type,
        alertKind: alertState.alertKind,
        message: alertState.message,
        showAlert,
        hideAlert,
      }}
    >
      {children}
    </AlertContext.Provider>
  );
}

export function useAlert() {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error("useAlert must be used within AlertProvider");
  }
  return context;
}
